import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../model/requestUtils";
import { ENDPOINTS, AUTH_QUERY_CACHE } from "../model/authEndpoints";
import type { ApiError } from "../model/shared";
import type { UserSettings } from "../model/type";
import { useAuthStore } from "./useAuthStore";

export const useSaveUserSettings = () => {
  const queryClient = useQueryClient();
  return useMutation<
    UserSettings,
    ApiError,
    { primaryColor?: string; secondaryColor?: string; language?: string }
  >({
    mutationKey: [AUTH_QUERY_CACHE.SAVE_USER_SETTINGS],
    mutationFn: async (data) => {
      const response = await apiClient.put<UserSettings>(
        ENDPOINTS.userSettings,
        data,
      );
      return response.data;
    },
    onSuccess: (data) => {
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        useAuthStore.getState().setUser({ ...currentUser, settings: data });
      }
      queryClient.invalidateQueries({
        queryKey: [AUTH_QUERY_CACHE.USER_SETTINGS],
      });
    },
  });
};

export const useUploadUserLogo = () => {
  return useMutation<{ url: string }, ApiError, File>({
    mutationKey: [AUTH_QUERY_CACHE.UPLOAD_USER_LOGO],
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await apiClient.post<{ url: string }>(
        ENDPOINTS.userSettingsLogo,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        useAuthStore.getState().setUser({
          ...currentUser,
          settings: { ...currentUser.settings, logoUrl: data.url },
        });
      }
    },
  });
};

export const useDeleteUserLogo = () => {
  return useMutation<void, ApiError, void>({
    mutationKey: [AUTH_QUERY_CACHE.DELETE_USER_LOGO],
    mutationFn: async () => {
      await apiClient.delete(ENDPOINTS.userSettingsLogo);
    },
    onSuccess: () => {
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        useAuthStore.getState().setUser({
          ...currentUser,
          settings: { ...currentUser.settings, logoUrl: undefined },
        });
      }
    },
  });
};
